import clsx from 'clsx';
import styles from './styles.module.css';

import { Layout } from 'components/layout';
import { CardsMedium } from 'components/slider/cards/medium';

import { CustomMetadata } from 'components/metadata';

import { allCasinos } from 'constants/routes-breadcrumbs';

import { LoadMoreButton } from 'components/load-more-button';

import { useCasinosPagination, useCurrentRegion } from 'hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SITE_URL } from 'api/constants';
import { allMetadata } from 'constants/metadata';

import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { Breadcrumbs } from 'components/bredcrambs';
import { FAQComponent } from 'components/casino-faq';
import { Title } from 'components/title';
import { Routes } from 'constants/routes';
import { ICasino, IFilter, IRegionItem, ITagFilterItem } from 'constants/types';
import { useRouter } from 'next/router';
import { TagsFilters } from '../casino/casino-navigations';
import { Search } from '../home/search';
import { CasinosDescription } from './description';
import { Filters } from './filters';

type Props = {
  name?: string;
  title?: string;
  sortBy?: string;
  slotId?: string;
  casinoIds?: string;
  casinos?: ICasino[];
  description?: string;
  canonicalUrl?: string;
  collection?: string | null;
  licenses?: string[];
  software?: string[];
  countries?: IRegionItem[];
  casinosCount?: number;
};

const capitalizeFirstLetter = (string: string | null | undefined) => {
  if (!string || !string?.length) return null;

  return string?.charAt(0)?.toUpperCase() + string?.slice(1);
};

const toAbsoluteUrl = (url?: string) => {
  if (!url) {
    return undefined;
  }

  if (/^https?:\/\//.test(url)) {
    return url;
  }

  return `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const serializeQuery = (query: Record<string, unknown>) => {
  const params = new URLSearchParams();

  Object.keys(query)
    .sort()
    .forEach((key) => {
      const value = query[key];

      if (!value) {
        return;
      }

      const values = Array.isArray(value) ? value : [value];

      values.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          params.append(key, String(item));
        }
      });
    });

  return params.toString();
};

const NoCasinos = () => {
  return (
    <div className={styles.empty_data}>
      <p className={styles.message}>{'No casinos matching your criteria.'}</p>
    </div>
  );
};

const collectionsFilters: ITagFilterItem[] = [
  {
    id: 'trusted-casinos',
    icon: <Icon.Success />,
    text: 'Trusted Casinos',
    type: 'navigate',
    destination: Routes.casinoCollections('trusted-casinos'),
  },
  {
    id: 'top-10-casinos',
    icon: <Icon.Star fill="white" width="14" height="14" />,
    text: 'Top 10 Casinos',
    type: 'navigate',
    destination: Routes.casinoCollections('top-10-casinos'),
  },
  {
    id: 'crypto-casinos',
    icon: <Icon.Cryptocoin />,
    text: 'Crypto Casinos',
    type: 'navigate',
    destination: Routes.casinoCollections('crypto-casinos'),
  },
  {
    id: 'new-casinos',
    icon: <Icon.CasinosNew />,
    text: 'New Casinos',
    type: 'navigate',
    destination: Routes.casinoCollections('new-casinos'),
  },
  {
    id: 'blacklisted-casinos',
    icon: <Icon.Stop />,
    text: 'Blacklisted Casinos',
    type: 'navigate',
    destination: Routes.casinoCollections('blacklisted-casinos'),
  },
  // {
  //   id: 'more',
  //   icon: <Icon.Menu />,
  //   text: 'More',
  //   type: 'more',
  //   subItems: [
  //     {
  //       id: 'best-casinos',
  //       text: 'Best Casinos',
  //       icon: <></>,
  //       type: 'navigate',
  //       destination: Routes.casinoCollections('best-casinos-apps'),
  //     },
  //     {
  //       id: 'casino-maximum-rtp',
  //       text: 'Casino Maximum RTP',
  //       icon: <></>,
  //       type: 'navigate',
  //       destination: Routes.casinoCollections('casino-maximum-rtp'),
  //     },
  //   ],
  // },
];

// const tagsFilters: ITagFilterItem[] = [
//   {
//     id: 'highrtp',
//     icon: <Icon.Like width={13} height={13} />,
//     text: 'High RTP',
//   },
//   {
//     id: 'averagertp',
//     icon: <Icon.DislikeSecond />,
//     text: 'Average RTP',
//   },
//   {
//     id: 'lowrtp',
//     icon: <Icon.AlertSecond />,
//     text: 'Low RTP',
//   },
//   {
//     id: 'trusted',
//     icon: <Icon.Veryfied color="#1AFF06" />,
//     text: 'Trusted',
//   },
//   {
//     id: 'blacklisted',
//     icon: <Icon.Blacklisted />,
//     text: 'Blacklisted',
//   },
//   {
//     id: 'crypto',
//     icon: <Icon.Cryptocoin />,
//     text: 'Crypto',
//   },
// ];

export interface SelectedOrdinaryFilters {
  highrtp: boolean;
  averagertp: boolean;
  lowrtp: boolean;
  trusted: boolean;
  blacklisted: boolean;
  crypto: boolean;
}

export const AllCasinosPage = ({
  sortBy = 'popular',
  collection = null,
  licenses,
  software,
  countries,
  title,
  description,
  canonicalUrl,
  casinos: initialCasinos = [],
  casinosCount: initialCasinosCount,
}: Props) => {
  const router = useRouter();
  const { currentRegion } = useCurrentRegion();

  const { query } = router;

  const parseInitialQueryFilters = (allowedKeys: string[] = []) => {
    const initialQueryFilters: any = {};

    Object.keys(query).forEach((key) => {
      if (query[key] && allowedKeys.includes(key)) {
        initialQueryFilters[key] = query[key] as string;
      }
    });

    return initialQueryFilters;
  };

  const defaultSelectedFilters: any = useMemo(() => {
    return {
      sortBy: 'popular',
      ...(currentRegion ? { countries: currentRegion?._id } : {}),
    };
  }, [currentRegion]);

  const [submittedTimestamp, setSubmittedTimestamp] = useState<number>(0);
  const [selectedFilters, setSelectedFilters] = useState<any>(
    parseInitialQueryFilters([
      'sortBy',
      'countries',
      'licenses',
      'player_ratings',
      'software',
    ])
  );
  const selectedFiltersRef = useRef(selectedFilters);
  // const [selectedTagsFilters, setSelectedTagsFilters] = useState(
  //   parseInitialQueryFilters([
  //     'highrtp',
  //     'averagertp',
  //     'lowrtp',
  //     'trusted',
  //     'blacklisted',
  //     'crypto',
  //   ])
  // );

  const [searchQuery, setSearchQuery] = useState('');

  const {
    page,
    setPage,
    isFetching,
    data: casinosPagination,
    isLast,
  } = useCasinosPagination({
    collection,
    sortBy,
    filters: selectedFilters,
    searchQuery,
    submittedTimestamp,
    initialData: initialCasinos,
    initialCount: initialCasinosCount,
  });

  const handleSearch = (searchValue: string) => {
    setSearchQuery(searchValue);
  };

  const updateQueryParams = useCallback(
    (filters: object) => {
      const updatedQuery = {};

      Object.keys(filters).forEach((key) => {
        if ((filters as any)[key]) {
          (updatedQuery as any)[key] = (filters as any)[key];
        }
      });

      if (serializeQuery(query) === serializeQuery(updatedQuery)) {
        return;
      }

      router.replace(
        {
          pathname: router.pathname,
          query: updatedQuery,
        },
        undefined,
        {
          shallow: true,
        }
      );
    },
    [query, router]
  );

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  const applyFilters = useCallback(() => {
    updateQueryParams({ ...selectedFiltersRef.current });
  }, [updateQueryParams]);

  useEffect(() => {
    if (!submittedTimestamp) {
      return;
    }

    applyFilters();
  }, [applyFilters, submittedTimestamp]);

  const filters: IFilter[] = useMemo(
    () => [
      {
        key: 'sortBy',
        isSingle: true,
        text: `Sort by: ${
          capitalizeFirstLetter(selectedFilters?.sortBy) ?? 'Popular'
        }`,
        title: 'Sort by',
        type: 'common',
        items: [
          { _id: 'popular', name: 'Most popular' } as IRegionItem,
          { _id: 'new', name: 'New casinos' } as IRegionItem,
        ],
      },
      /* {
        key: 'countries',
        isSingle: true,
        text: selectedFilters.countries?.length
          ? (capitalizeFirstLetter(
              countries?.find(
                (country) => country._id === selectedFilters.countries
              )?.name
            ) ?? 'Countries')
          : (capitalizeFirstLetter(
              countries?.find(
                (country) => country._id === defaultSelectedFilters.countries
              )?.name
            ) ?? 'Countries'),
        items: countries,
        title: 'Choosing a country',
        type: 'countries',
        icon: selectedFilters.countries?.length
          ? countries?.find(
              (country) => country._id === selectedFilters.countries
            )?.imageUrl
          : countries?.find(
              (country) => country._id === defaultSelectedFilters.countries
            )?.imageUrl,
      },
      {
        key: 'licenses',
        text: selectedFilters?.licenses?.length
          ? `Licence (${selectedFilters?.licenses?.length})`
          : 'Licence',
        items: licenses || [],
        title: selectedFilters?.licenses?.length
          ? `Licence (${selectedFilters?.licenses?.length})`
          : 'Licence',
        type: 'Licence',
      },*/
      {
        key: 'player_ratings',
        isSingle: true,
        value: 'player_ratings',
        text:
          capitalizeFirstLetter(selectedFilters?.player_ratings) ??
          'Player Ratings',
        type: 'rating',
        items: [
          {
            _id: 'positive',
            name: 'Positive',
            icon: <Icon.Like />,
            description: 'More Than 60% of The Reviews Are Good.',
          } as IRegionItem,
          {
            _id: 'negative',
            name: 'Negative',
            icon: <Icon.Dislike style={{ marginTop: 4 }} fill="#F9530B" />,
            description: 'Less Than 60% of The Reviews Are Good.',
          } as IRegionItem,
        ],
        title: 'Player Ratings',
      },
      /*{
        key: 'software',
        text: selectedFilters?.software?.length
          ? `Software (${selectedFilters?.software?.length})`
          : 'Software',
        items: software || [],
        title: selectedFilters?.software?.length
          ? `Software (${selectedFilters?.software?.length})`
          : 'Software',
        type: 'software',
      },*/
      {
        key: 'rtp',
        isSingle: true,
        value: 'rtp',
        text:
          (selectedFilters?.rtp === 'hight' && 'High RTP') ||
          (selectedFilters?.rtp === 'average' && 'Average RTP') ||
          (selectedFilters?.rtp === 'low' && 'Low RTP') ||
          'RTP Checking',
        type: 'rtp',
        items: [
          {
            _id: 'hight',
            name: '95% Hight RTP',
            description: 'The maximum RTP of casino games.',
          } as IRegionItem,
          {
            _id: 'average',
            name: '92% Average RTP',
            description: 'The RTP of games is below the maximum.',
          } as IRegionItem,
          {
            _id: 'low',
            name: '85% Low RTP',
            description: 'The RTP Is Below Average And Close To The Minimum.',
          } as IRegionItem,
        ],
        title: 'RTP Checking',
      },
    ],
    [selectedFilters]
  );

  const metadata = useMemo(() => {
    const pageUrl = toAbsoluteUrl(canonicalUrl);
    const trimmedTitle = title?.trim();

    return {
      ...allMetadata.allCasinos,
      title: trimmedTitle
        ? `${trimmedTitle} Slot for Real Money - Online Casinos`
        : allMetadata.allCasinos.title,
      description: description ?? allMetadata.allCasinos.description,
      canonicalUrl: pageUrl ?? allMetadata.allCasinos.canonicalUrl,
      url: pageUrl ?? allMetadata.allCasinos.url,
    };
  }, [canonicalUrl, description, title]);

  return (
    <div className={styles.container}>
      <CustomMetadata metadata={metadata} />
      <Layout hasFooter isSearchInputMobileVisible={false} bgVariant="home">
        <main className={clsx(styles.main, styles.main_collection)}>
          <div className={styles.breadcrumbs}>
            <Breadcrumbs items={[...allCasinos]} />
          </div>
          <Title
            variant={'twocolumns'}
            text="Choose the best online casinos with high RTP and good reviews."
            description="Before the game, we recommend looking at not only the real return to player percentage, but also the ratings of the casino players ng at not only the real return to player percentage, but also the ratings"
            gap={10}
            navigationBar={
              <TagsFilters
                items={collectionsFilters}
                selectedOrdinaryFilters={''}
                setSelectedOrdinaryFilters={() => {}}
                setFieldValue={() => {}}
                variant={'transparent'}
              />
            }
          />
          <Search
            variant={'allcasinos'}
            placeholder="Check out the RTP online casino..."
            rounded
            onSearch={handleSearch}
          />

          <div>
            <div className={styles.filter}>
              <div className={styles.multiTouchFilter}>
                {filters.map((filter, index) => (
                  <Filters
                    filterItemClassname={styles.filter_item}
                    onSubmit={() => {
                      setSubmittedTimestamp(Date.now());
                    }}
                    dirty={false}
                    isSubmitting={false}
                    key={index}
                    filter={filter}
                    setFieldValue={(key, value) => {
                      setSelectedFilters((prev: any) => ({
                        ...prev,
                        [key]: value,
                      }));
                    }}
                    defaultValues={defaultSelectedFilters}
                    values={selectedFilters}
                  />
                ))}
              </div>

              <div className={styles.clearFilters}>
                {JSON.stringify(selectedFilters) !==
                  JSON.stringify(defaultSelectedFilters) &&
                  JSON.stringify(selectedFilters) !== '{}' && (
                    <Button
                      className={styles.clearFilter}
                      onClick={() => {
                        setSelectedFilters(defaultSelectedFilters);
                        setSubmittedTimestamp(Date.now());
                      }}
                      type="button"
                      text={'Clear Filters'}
                      icon={
                        <Icon.Close fill="#caf413" width={16} height={16} />
                      }
                    />
                  )}
              </div>
              {/* <TagsFilters
                items={tagsFilters}
                selectedOrdinaryFilters={selectedTagsFilters as any}
                setSelectedOrdinaryFilters={setSelectedTagsFilters as any}
                setFieldValue={() => {}}
              /> */}
            </div>

            <CardsMedium key={searchQuery} data={casinosPagination} />
            {!casinosPagination?.length && !isFetching && <NoCasinos />}
            {!isLast && (
              <>
                <LoadMoreButton
                  isAuto={false}
                  isVisible={!isLast}
                  isFetching={isFetching}
                  onClick={() => setPage(page + 1)}
                  className={styles.load_more_button}
                  description="Load more"
                  variant="transparent"
                />
                <div className={styles.bottom} />
              </>
            )}
          </div>

          <CasinosDescription />

          <FAQComponent
            customFaq={[
              {
                title:
                  'How can I use casino ratings to choose the best platform for real money?',
                description:
                  'Casino ratings provide valuable insights into game quality, customer support, payment reliability, and overall trustworthiness. By comparing ratings, you can identify platforms that suit your preferences.',
              },
              {
                title: 'What makes a casino trustworthy and worth playing at?',
                description:
                  'A trustworthy casino is licensed by reputable authorities, offers fair games with high RTPs, has secure payment methods, and responds promptly to player concerns.',
              },
              {
                title:
                  'How do I know if a casino accepts players from my country?',
                description:
                  'Use our country selection tool to filter casinos that accept players from your region, ensuring compliance with local gambling regulations.',
              },
              {
                title: 'What should I do if a casino is on the blacklist?',
                description:
                  'Avoid playing at blacklisted casinos as they may have unresolved complaints, unfair terms, or fraudulent practices. Always stick to reputable platforms in our rating.',
              },
              {
                title:
                  'Why is it important to verify a casino’s license and certification?',
                description:
                  'Licenses and certifications ensure that a casino operates legally and adheres to industry standards for fairness and player protection.',
              },
              {
                title: 'How can I evaluate a casino’s bonus offers?',
                description:
                  'Look at the terms and conditions of bonuses, including wagering requirements, maximum withdrawal limits, and game restrictions, to determine their true value.',
              },
              {
                title: 'Are online casinos safe for real money play?',
                description:
                  'Yes, as long as you choose casinos with strong security measures, such as SSL encryption, and those listed in our trusted rating.',
              },
              {
                title: 'Can I test casino games before wagering real money?',
                description:
                  'Many online casinos offer demo versions of games, allowing you to try them for free and get familiar with gameplay before betting.',
              },
              {
                title: 'What are the benefits of high-RTP casinos?',
                description:
                  'High-RTP casinos typically offer games with better odds of winning over time, providing more value for your wagers.',
              },
              {
                title: 'How does reading player reviews help me avoid scams?',
                description:
                  'Player reviews provide firsthand accounts of issues like payment delays or poor customer service, helping you avoid platforms with a history of complaints.',
              },
            ]}
          />
        </main>
      </Layout>
    </div>
  );
};
